import type { ReputationAnalyticsDto } from '@up2stay/up2promo-api-client';

import { escapeCsvCell } from '@/core/lib/csv-cell';

const DELIMITER = ';';

export type AnalyticsCsvLabels = {
  metric: string;
  value: string;
  totalReviews: string;
  reputationScore: string;
  averageRating: string;
  positiveShare: string;
  responseRate: string;
  location: string;
  reviews: string;
  neutralShare: string;
  negativeShare: string;
};

function percent(rate: number): string {
  return `${Math.round(rate * 100)} %`;
}

/** BOM-prefixed, semicolon-delimited CSV: KPI summary block + one row per location. */
export function buildAnalyticsCsv(
  data: ReputationAnalyticsDto,
  locationNames: ReadonlyMap<string, string>,
  labels: AnalyticsCsvLabels,
): string {
  const share = (part: number, whole: number) => (whole > 0 ? part / whole : 0);
  const summary: string[][] = [
    [labels.metric, labels.value],
    [labels.totalReviews, String(data.totalReviews)],
    [labels.reputationScore, String(data.score.score)],
    [labels.averageRating, data.averageRating.toFixed(2)],
    [
      labels.positiveShare,
      percent(share(data.sentimentSplit.positive, data.totalReviews)),
    ],
    [labels.responseRate, percent(data.responseRateWithText)],
  ];
  const locations: string[][] = [
    [
      labels.location,
      labels.reputationScore,
      labels.reviews,
      labels.averageRating,
      labels.positiveShare,
      labels.neutralShare,
      labels.negativeShare,
      labels.responseRate,
    ],
    ...data.locations.map((row) => {
      const total = row.sentimentSplit;
      const sum = total.positive + total.neutral + total.negative;
      return [
        locationNames.get(row.locationId) ?? row.locationId,
        String(row.score.score),
        String(row.totalReviews),
        row.averageRating.toFixed(2),
        percent(share(total.positive, sum)),
        percent(share(total.neutral, sum)),
        percent(share(total.negative, sum)),
        percent(row.responseRateWithText),
      ];
    }),
  ];

  const toLine = (cells: string[]) =>
    cells.map((cell) => escapeCsvCell(cell, DELIMITER)).join(DELIMITER);
  const lines = [...summary.map(toLine), '', ...locations.map(toLine)];
  return `﻿${lines.join('\n')}\n`;
}

export function analyticsCsvFilename(date: Date = new Date()): string {
  return `reputation-analytics-${date.toISOString().slice(0, 10)}.csv`;
}
